<svelte:options accessors />

<script lang="ts">
	import InfoCircle from '$lib/assets/images/icons/information.svg?component';
	import { fade, fly } from 'svelte/transition';
	import { tooltip, type TooltipProps } from '$lib/actions/tooltip';
	import { onMount } from 'svelte';
	import { hasTouchSupport } from '$lib/utils/helpers/hasTouchSupport';
	import { twMerge } from 'tailwind-merge';
	import { isBrowserWebkit } from '$lib/utils/helpers/deviceSupport';

	export let id = '';
	export let label = '';
	export let name = '';
	export let value = '';
	export let inputmode = 'text';
	export let type = 'text';
	export let required: boolean = false;
	export let helperText: string = '';
	export let placeholder = label;
	export let error: string | null = null;
	export let focusOnMount: boolean = false;
	export let showUpLabel: boolean = false;
	export let light: boolean = false;
	export let borderless: boolean = false;
	export let inputClass: string = '';

	let className = '';
	export { className as class };

	const helperTooltipConfig: TooltipProps = {
		content: helperText || '',
		placement: 'bottom',
		animation: 'shift-away'
	};

	let overlayWidth: number = 0;
	let containerWidth: number = 0;

	$: overlayText = value || placeholder || '';

	let helperLeft: number = 0;
	$: helperLeft =
		containerWidth - (overlayWidth - 20) > 20 ? overlayWidth + 10 : containerWidth - 20;
	let input: HTMLInputElement;

	export const getInput = () => input;

	let hasTouch: boolean;
	function handleHelperClick() {
		if (!hasTouch) input.focus();
	}

	function handleInputBlur(e: Event) {
		if (isWebkit) {
			(e.target as HTMLInputElement).dispatchEvent(new Event('change', { bubbles: true }));
		}
	}

	let isWebkit = false;
	onMount(() => {
		isWebkit = isBrowserWebkit();
		hasTouch = hasTouchSupport();
		if (focusOnMount) {
			input.focus();
		}
	});
</script>

<div class={twMerge('relative w-full', className)}>
	{#if value || showUpLabel}
		<slot name="label">
			<div
				transition:fly={{ y: 10 }}
				class="absolute -top-4 flex items-center gap-2 {light ? 'text-white' : 'text-caption'}"
			>
				<label class="text-sm" for={id}>{label || placeholder}</label>
				{#if helperText}
					<span
						class="box-border"
						use:tooltip={helperTooltipConfig}
						on:keyup={() => {}}
						on:click|preventDefault={handleHelperClick}
					>
						<InfoCircle
							class="h-3 cursor-pointer {light ? 'stroke-white' : 'stroke-caption'} stroke-[1.4]"
						/>
					</span>
				{/if}
			</div>
		</slot>
	{/if}
	<div
		class="flex w-full items-center gap-2 {!borderless ? 'border-b-[1px]' : ''} {light
			? 'border-white'
			: 'border-dark'}"
	>
		{#if $$slots.default}
			<div>
				<slot />
			</div>
		{/if}
		<div
			class="relative flex w-full items-center justify-items-center pr-8"
			bind:clientWidth={containerWidth}
		>
			{#if $$slots.left}
				<div class="grow-0 pr-2">
					<slot name="left" />
				</div>
			{/if}
			<input
				{name}
				{required}
				{inputmode}
				{...{ type }}
				autocomplete="off"
				bind:this={input}
				bind:value
				class={twMerge(
					`input-wrapper w-full flex-1 appearance-none bg-transparent py-2 outline-none ${
						light ? 'placeholder:text-white' : 'placeholder:text-caption'
					}`,
					inputClass
				)}
				on:input
				on:focus
				on:change
				on:click
				on:blur
				on:blur={handleInputBlur}
				{placeholder}
			/><br />
			<p class="invisible absolute top-2" bind:clientWidth={overlayWidth}>{overlayText}</p>
			{#if !value && helperText}
				<div
					style:left={helperLeft + 'px'}
					class="content absolute cursor-pointer items-center justify-items-start"
				>
					<span
						class="box-border"
						use:tooltip={helperTooltipConfig}
						on:keyup={() => {}}
						on:click|preventDefault={handleHelperClick}
					>
						<InfoCircle
							class="h-4 cursor-pointer {light ? 'stroke-white' : 'stroke-caption'} stroke-[1.2]"
						/>
					</span>
				</div>
			{/if}
			{#if value && $$slots.after}
				<div
					transition:fade={{ duration: 100 }}
					class="pointer-events-none absolute"
					style:left={helperLeft + 'px'}
				>
					<slot name="after" />
				</div>
			{/if}
		</div>
	</div>
	{#if error}
		<span class="w-full py-2 text-xs text-red-500">{error}</span>
	{/if}
</div>

<style>
</style>
